import React, { Fragment, useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from 'react-bootstrap';
import CompanyForm from './CompanyForm';
import DealForm from './DealForm';
import CompanySelect from './CompanySelect';
import { CompanyResource, DealResource } from '../../common/entities';
import AuthForm from './AuthForm';
import { BasicAuth } from '../../common/interfaces';
import http from '../../http';
import CompanyCard from '../../common/components/CompanyCard';
import DealsDataService from '../../common/services/DealsDataService';
import { isArray } from 'lodash';
import AuthDataService from '../../common/services/AuthDataService';

enum Step {
  AuthForm,
  NewOrExistingCompany,
  CompanyForm,
  DealForm,
  Done,
}
/**
 * @description High level component for dealing with Briefed.in marketplace.
 * @return {JSX.Element}
 */
const DealCreate = (): JSX.Element => {
  const [company, setCompany] = useState<CompanyResource>();
  const [deal, setDeal] = useState<DealResource>();
  const [step, setStep] = useState<Step>(Step.AuthForm);
  const [busy, setBusy] = useState<boolean>(false);
  const [basicAuth, setBasicAuth] = useState<BasicAuth>();
  const [allowNewCompany, setAllowNewCompany] = useState<boolean>(false);
  const [hasAuth, setHasAuth] = useState<boolean>(false);

  const checkAuth = async () => {
    console.log('has auth?');
    setBusy(true);
    try {
      // AuthDataService.setAuth(data);
      await AuthDataService.get();
      setBusy(false);
      // props.onSuccess(data);
      setHasAuth(true);
      setStep(Step.NewOrExistingCompany);
    } catch (err) {
      // console.error('.', err);
      // setErrorMessage('Invalid credentials provided.');
      setBusy(false);
      setHasAuth(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  if (busy) {
    return (
      <Container className="mt-100px">
        <Spinner animation={'border'} />
      </Container>
    );
  }
  return (
    <Fragment>
      <Container className="mt-100px">
        {step === Step.AuthForm ? (
          <Fragment>
            <p>Step {Step.AuthForm + 1}: Sign in</p>
            <AuthForm
              onSuccess={(credentials) => {
                setBasicAuth(credentials);
                http.defaults.auth = {
                  username: credentials.user,
                  password: credentials.pass,
                };
                setStep(Step.NewOrExistingCompany);
              }}
            />
          </Fragment>
        ) : null}
        {step === Step.NewOrExistingCompany ? (
          <Fragment>
            <p>Step {Step.NewOrExistingCompany + 1}: Choose company</p>
            <p>Does the company already exist?</p>
            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Search for a company</Form.Label>
                  <Row>
                    <Col md={8}>
                      <CompanySelect
                        id={'company-select'}
                        onSelect={async (selected) => {
                          const company = selected[0] as CompanyResource;
                          setCompany(company);
                          if (!company) {
                            setDeal(undefined);
                            return;
                          }
                          // lookup latest deals
                          const response =
                            await DealsDataService.getAllWithMeta({
                              companyId: company.id,
                            });
                          if (
                            isArray(response.data) &&
                            response.data.length > 0
                          ) {
                            setDeal(response.data[0] as DealResource);
                          }
                        }}
                        onTouched={() => setAllowNewCompany(true)}
                      />
                    </Col>
                    <Col md={4}>
                      <Button
                        disabled={!allowNewCompany}
                        className="align-content-center"
                        variant={'primary'}
                        size={'sm'}
                        onClick={() => {
                          setStep(Step.CompanyForm);
                          setCompany(undefined);
                        }}
                      >
                        New Company
                      </Button>
                    </Col>
                  </Row>
                </Form.Group>
              </Col>
              <Col>
                {company ? (
                  <Fragment>
                    <CompanyCard company={company} deal={deal} />
                    <div className={'text-center'}>
                      <Button
                        className="mt-3 justify-center"
                        variant={'primary'}
                        onClick={() => setStep(Step.DealForm)}
                      >
                        Use this company
                      </Button>
                    </div>
                  </Fragment>
                ) : null}
              </Col>
            </Row>
          </Fragment>
        ) : null}
        {step === Step.CompanyForm ? (
          <Fragment>
            <p>Step {Step.CompanyForm + 1}: New company</p>

            <CompanyForm
              onSave={async (company) => {
                console.log('save', company);
                setCompany(company);
                window.scrollTo(0, 0);
                setStep(Step.DealForm);
              }}
            />
          </Fragment>
        ) : null}
        {step === Step.DealForm ? (
          <Fragment>
            <p>Step {Step.DealForm + 1}: New deal</p>
            <DealForm
              company={company as CompanyResource}
              onSave={async (deal) => {
                console.log('save', deal);
                setStep(Step.Done);
              }}
            />
          </Fragment>
        ) : null}
        {step === Step.Done ? (
          <Card body>
            <p>Step {Step.Done + 1}: Done</p>

            <Alert variant={'success'}>
              <Alert.Heading>Success</Alert.Heading>
              <p>Deal successfully added.</p>
              <Button
                onClick={() => {
                  // clear company and restart
                  setCompany(undefined);
                  setStep(Step.NewOrExistingCompany);
                }}
              >
                New Deal
              </Button>
            </Alert>
          </Card>
        ) : null}
      </Container>
    </Fragment>
  );
};

export default DealCreate;
